import React, { useState } from 'react';

/**
 * Manual Address Input Component
 * Fallback when Google Maps is not available
 */
const ManualAddressInput = ({ onAddressSelect, onCancel }) => {
  const [addressData, setAddressData] = useState({
    street: '',
    area: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setAddressData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!addressData.street.trim()) {
      newErrors.street = 'Street address is required';
    }
    if (!addressData.area.trim()) {
      newErrors.area = 'Area/Locality is required';
    }
    if (!addressData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!addressData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!addressData.pincode.trim()) {
      newErrors.pincode = 'PIN code is required';
    } else if (!/^\d{6}$/.test(addressData.pincode.trim())) {
      newErrors.pincode = 'Please enter a valid 6-digit PIN code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const fullAddress = {
      ...addressData,
      formatted: `${addressData.street}, ${addressData.area}, ${addressData.city}, ${addressData.state} ${addressData.pincode}`,
      coordinates: null // No coordinates for manual input
    };

    if (onAddressSelect) {
      onAddressSelect(fullAddress);
    }
  };

  return (
    <div className="manual-address-input">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Enter Address Manually</h6>
          {onCancel && (
            <button 
              type="button" 
              className="btn-close" 
              onClick={onCancel}
              aria-label="Close"
            ></button>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label">
                  Street Address <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.street ? 'is-invalid' : ''}`}
                  placeholder="House/Flat No., Building Name, Street"
                  value={addressData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                />
                {errors.street && (
                  <div className="invalid-feedback">{errors.street}</div>
                )}
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">
                  Area/Locality <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.area ? 'is-invalid' : ''}`}
                  placeholder="Area, Locality, Sector"
                  value={addressData.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                />
                {errors.area && (
                  <div className="invalid-feedback">{errors.area}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  City <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                  placeholder="City"
                  value={addressData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
                {errors.city && (
                  <div className="invalid-feedback">{errors.city}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  State <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                  placeholder="State"
                  value={addressData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
                {errors.state && (
                  <div className="invalid-feedback">{errors.state}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  PIN Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.pincode ? 'is-invalid' : ''}`}
                  placeholder="6-digit PIN code"
                  value={addressData.pincode}
                  onChange={(e) => handleInputChange('pincode', e.target.value)}
                  maxLength={6}
                />
                {errors.pincode && (
                  <div className="invalid-feedback">{errors.pincode}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Country</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              {onCancel && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
              <button 
                type="submit" 
                className="btn btn-primary"
                style={{backgroundColor: '#2A632D', borderColor: '#2A632D'}}
              >
                Use This Address
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManualAddressInput;
